import {
  productErrorCode,
  productErrorStatus,
  type ProductApi,
  type ProductRuntimeInstance,
  type ProductRuntimeWaitReason
} from "../product-api.js";

// WHY [BOL-SELF-SERVE-WEB-001]: a brand-new business signs in before its own
// workspace pod exists. The live R56 flow threw "The selected business runtime
// is not ready." and stopped there, so every self-serve signup dead-ended
// (plan R763/R766). This owner asks the server for the workspace once (the
// request is idempotent: an existing row is returned unchanged), then polls
// the authenticated subscription snapshot until the server reports RUNNING,
// and only then selects it. It never grants access itself, never falls back
// to another business, and stops on the server's typed refusals.
// Server facts this relies on (live API, read 2026-09-25): every DomainError
// is HTTP 409 except FORBIDDEN (403); POST /api/product/runtime-instance needs
// `subscription:manage`, which only an OWNER has; select refuses with
// RUNTIME_INSTANCE_MISSING / _NOT_RUNNING / _FORBIDDEN.
// Falsifier: a MISSING/NOT_RUNNING sign-in still dead-ends, a refused or
// cancelled business keeps polling, a non-owner is told they have no access,
// or the page reloads before RUNNING or after the person left the screen.

export type WorkspacePreparation =
  | { phase: "REQUESTING" }
  | { phase: "QUEUED"; waitReason: ProductRuntimeWaitReason | null; longWait: boolean; unreachable: boolean }
  | { phase: "STARTING"; longWait: boolean; unreachable: boolean }
  | { phase: "OPENING" };

export type WorkspacePreparationStopReason =
  | "CANCELLED_SUBSCRIPTION" | "NO_ACCESS" | "OWNER_MUST_START" | "PAUSED" | "CLOSED" | "SIGNED_OUT";

export class WorkspacePreparationStopped extends Error {
  constructor(readonly reason: WorkspacePreparationStopReason) {
    super(workspacePreparationStopMessage(reason));
    this.name = "WorkspacePreparationStopped";
  }
}

// 3 s at first, then slowing to 10 s: fast enough to open a pod that becomes
// healthy within a minute, light enough that a queued signup left open for an
// hour costs one small authenticated read every 10 s (API limit: 300/min/IP).
export const PREPARATION_POLL_DELAYS_MS: readonly number[] = [3_000, 3_000, 5_000, 7_000, 10_000];
export const PREPARATION_LONG_WAIT_MS = 120_000;
// Consecutive failed reads before the screen admits it cannot reach the server.
export const PREPARATION_UNREACHABLE_AFTER = 3;

const SETTING_UP = "Setting up your private workspace. This usually takes a few minutes.";
const LONG_WAIT_NOTE = "You can close this page and sign in later. We’ll keep setting up your workspace.";
const UNREACHABLE_NOTE = "We’re having trouble reaching our server. We’ll keep trying.";

export function preparationDelay(attempt: number): number {
  const last = PREPARATION_POLL_DELAYS_MS[PREPARATION_POLL_DELAYS_MS.length - 1] ?? 10_000;
  return PREPARATION_POLL_DELAYS_MS[Math.max(0, attempt)] ?? last;
}

export function isWorkspaceNotReady(cause: unknown): boolean {
  const code = productErrorCode(cause);
  return code === "RUNTIME_INSTANCE_MISSING" || code === "RUNTIME_INSTANCE_NOT_RUNNING";
}

export function isPreparationAbort(cause: unknown): boolean {
  return typeof cause === "object" && cause !== null && "name" in cause && cause.name === "AbortError";
}

// Plain words only: people never see "runtime" or "instance".
export function workspacePreparationCopy(progress: WorkspacePreparation): { message: string; note: string | null } {
  switch (progress.phase) {
    case "REQUESTING":
      return { message: SETTING_UP, note: null };
    case "QUEUED": {
      const note = progress.unreachable ? UNREACHABLE_NOTE : progress.longWait ? LONG_WAIT_NOTE : null;
      return progress.waitReason
        ? { message: "We’re busy right now. Your workspace will start as soon as there is room.", note: note ?? "Keep this page open, or sign in again later." }
        : { message: SETTING_UP, note };
    }
    case "STARTING":
      return { message: "Starting your workspace…", note: progress.unreachable ? UNREACHABLE_NOTE : progress.longWait ? LONG_WAIT_NOTE : null };
    case "OPENING":
      return { message: "Your workspace is ready. Opening it now…", note: null };
  }
}

export function workspacePreparationStopMessage(reason: WorkspacePreparationStopReason): string {
  switch (reason) {
    case "CANCELLED_SUBSCRIPTION":
      return "This business is cancelled, so its workspace can’t open. Contact the platform to reactivate this business.";
    case "NO_ACCESS":
      return "You don’t have access to this business.";
    case "OWNER_MUST_START":
      return "This business’s workspace hasn’t been set up yet. Ask the business owner to sign in once, then try again.";
    case "PAUSED":
      return "This workspace is paused. Contact the platform to reopen it.";
    case "CLOSED":
      return "This workspace has been closed. Contact the platform to reopen it.";
    case "SIGNED_OUT":
      return "Your sign-in expired. Please sign in again.";
  }
}

export function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(preparationAbortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(preparationAbortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function preparationAbortError(): Error {
  const error = new Error("Workspace preparation was cancelled.");
  error.name = "AbortError";
  return error;
}

export function throwIfPreparationAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw preparationAbortError();
}

// Retry only what can heal by waiting: no HTTP answer (network down or an
// unreadable proxy page), 5xx, 408/429, or the typed not-ready codes.
function isTransient(cause: unknown): boolean {
  const status = productErrorStatus(cause);
  if (status === null) return true;
  if (status >= 500 || status === 408 || status === 429) return true;
  return isWorkspaceNotReady(cause);
}

function stopFor(cause: unknown): WorkspacePreparationStopped | null {
  const code = productErrorCode(cause);
  const status = productErrorStatus(cause);
  if (code === "SUBSCRIPTION_CANCELLED") return new WorkspacePreparationStopped("CANCELLED_SUBSCRIPTION");
  if (code === "RUNTIME_INSTANCE_FORBIDDEN") return new WorkspacePreparationStopped("NO_ACCESS");
  if (status === 401 || code === "UNAUTHORIZED") return new WorkspacePreparationStopped("SIGNED_OUT");
  if (status === 403 || code === "FORBIDDEN") return new WorkspacePreparationStopped("NO_ACCESS");
  return null;
}

// Any other 4xx is a refusal the client cannot heal: stop with the server's
// own message instead of spinning (fail closed).
function refuse(cause: unknown): never {
  throw stopFor(cause) ?? cause;
}

export async function prepareBusinessWorkspace({
  apiClient, slug, onProgress, signal, delay = abortableDelay, now = Date.now
}: {
  apiClient: Pick<ProductApi, "requestRuntimeInstance" | "subscription" | "selectInstance">;
  slug: string;
  onProgress: (progress: WorkspacePreparation) => void;
  signal?: AbortSignal | undefined;
  delay?: (ms: number, signal?: AbortSignal) => Promise<void>;
  now?: () => number;
}): Promise<void> {
  const startedAt = now();
  let instance: ProductRuntimeInstance | null = null;
  // True once the server answered at least once (null then means "no row").
  let known = false;
  // Only an OWNER may create the request row; others can only wait for it.
  let mayRequest = true;
  let failures = 0;
  onProgress({ phase: "REQUESTING" });
  for (let attempt = 0; ; attempt += 1) {
    throwIfPreparationAborted(signal);
    try {
      instance = mayRequest && (!known || instance === null)
        ? (await apiClient.requestRuntimeInstance()).instance
        : (await apiClient.subscription()).subscription.runtimeInstance;
      known = true;
      failures = 0;
    } catch (cause) {
      throwIfPreparationAborted(signal);
      if (mayRequest && productErrorStatus(cause) === 403 && productErrorCode(cause) === "FORBIDDEN") {
        // A manager or team member cannot create the workspace. Read whether
        // the owner already did; never report this as "no access".
        mayRequest = false;
        known = false;
        continue;
      }
      if (!isTransient(cause)) refuse(cause);
      failures += 1;
    }
    throwIfPreparationAborted(signal);
    if (known && instance === null && !mayRequest) throw new WorkspacePreparationStopped("OWNER_MUST_START");
    if (instance?.state === "RUNNING") {
      try {
        const selected = await apiClient.selectInstance(slug);
        throwIfPreparationAborted(signal);
        if (selected.instance.slug === slug && selected.instance.state === "RUNNING") {
          onProgress({ phase: "OPENING" });
          return;
        }
      } catch (cause) {
        throwIfPreparationAborted(signal);
        // MISSING/NOT_RUNNING right after a RUNNING snapshot is a race; poll again.
        if (!isTransient(cause)) refuse(cause);
      }
    }
    if (instance?.state === "PAUSED") throw new WorkspacePreparationStopped("PAUSED");
    if (instance?.state === "RETIRED") throw new WorkspacePreparationStopped("CLOSED");
    const longWait = now() - startedAt >= PREPARATION_LONG_WAIT_MS;
    const unreachable = failures >= PREPARATION_UNREACHABLE_AFTER;
    if (instance?.state === "PROVISIONED" || instance?.state === "RUNNING") {
      onProgress({ phase: "STARTING", longWait, unreachable });
    } else {
      onProgress({ phase: "QUEUED", waitReason: instance?.waitReason ?? null, longWait, unreachable });
    }
    await delay(preparationDelay(attempt), signal);
  }
}
